<div class="unified-grid-wrapper" [ngClass]="config.className">
  <!-- Grid Filter Section -->
  @if (showGridFilter && filterFields.length > 0) {
    <div class="grid-filter-section">
      <app-grid-filter
        [fields]="filterFields"
        [showLabels]="filterConfig?.showLabels ?? false"
        [showClearButton]="filterConfig?.showClearButton ?? true"
        [showClearButtonText]="filterConfig?.showClearButtonText ?? true"
        [layout]="filterConfig?.layout ?? 'horizontal'"
        [initialValues]="filterConfig?.initialValues ?? {}"
        [globalDebounceTime]="filterConfig?.debounceTime ?? 500"
        (filterChange)="onGridFilterChange($event)"
        (filterClear)="onGridFilterClear()"
      >
      </app-grid-filter>
    </div>
  }

  <kendo-grid
    #kendoGrid
    [data]="gridData"
    [loading]="loading"
    [pageSize]="gridState.take || 10"
    [skip]="gridState.skip || 0"
    [pageable]="getPaginationConfig()"
    [sort]="gridState.sort || []"
    [filter]="getFilterConfig()"
    [group]="gridState.group || []"
    [sortable]="getSortableConfig()"
    [groupable]="getGroupableConfig()"
    [resizable]="config.resizable === true"
    [reorderable]="config.reorderable === true"
    [height]="getGridHeight()"
    [selectable]="getSelectableConfig()"
    [kendoGridSelectBy]="'id'"
    [selectedKeys]="selectedKeys"
    [rowClass]="getRowClass.bind(this)"
    (dataStateChange)="onDataStateChange($event)"
    (selectionChange)="onSelectionChange($event)"
    (cellClick)="onCellClick($event)"
    (groupChange)="onGroupChange($event || [])"
    class="custom-kendo-grid"
    [filterable]="getFilterableConfig()"
    scrollable="scrollable"
  >
    @if (isExcelExportEnabled()) {
      <kendo-grid-excel [fileName]="getExcelFileName()"></kendo-grid-excel>
    }
    @if (isPdfExportEnabled()) {
      <kendo-grid-pdf
        [fileName]="getPdfFileName()"
        [allPages]="true"
        [paperSize]="'A4'"
        [margin]="'2cm'"
        [landscape]="true"
      >
      </kendo-grid-pdf>
    }

    <!-- Dynamic column rendering based on column type -->
    @for (column of processedColumns; track column.field || column.title || $index) {
      @if (column.type !== 'actions' && !column.actions) {
        <kendo-grid-column
          [field]="column.field!"
          [title]="column.title || ''"
          [width]="column.width != null ? +column.width : 120"
          [minResizableWidth]="column.minWidth || 50"
          [maxResizableWidth]="column.maxWidth != null ? +column.maxWidth : 120"
          [locked]="column.locked === true"
          [lockable]="column.lockable === true"
          [hidden]="column.hidden === true"
          [sortable]="column.sortable === true"
          [filterable]="column.filterable === true"
          [groupable]="column.groupable === true"
          [format]="getColumnFormat(column)"
          [headerClass]="getHeaderClass(column)"
          [headerStyle]="column.headerStyle || {}"
          [class]="getColumnClass(column)"
          [style]="column.style || {}"
        >
          <!-- Image column template -->
          @if (column.type === 'image') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="image-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <img
                  [src]="getImageSrc(dataItem, column)"
                  [alt]="getImageAlt(dataItem, column)"
                  [title]="getImageTitle(dataItem, column)"
                  class="grid-image"
                  [ngClass]="getImageClass(column)"
                  [style.width]="column.imageConfig?.width || '40px'"
                  [style.height]="column.imageConfig?.height || '40px'"
                  (error)="onImageError($event, column)"
                />
              </div>
            </ng-template>
          }

          <!-- Boolean column template -->
          @else if (column.type === 'boolean') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span
                class="boolean-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <i
                  [class]="
                    dataItem[column.field!]
                      ? 'bi bi-check-circle text-success'
                      : 'bi bi-x-circle text-danger'
                  "
                  [title]="dataItem[column.field!] ? 'Active' : 'Inactive'"
                ></i>
              </span>
            </ng-template>
          }

          <!-- Date column template -->
          @else if (column.type === 'date') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span
                class="date-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                {{ formatDate(dataItem[column.field!], column.format || 'dd/MM/yyyy') }}
              </span>
            </ng-template>
          }

          <!-- Currency column template -->
          @else if (column.type === 'currency') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span
                class="currency-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                {{ formatCurrency(dataItem[column.field!], column.format) }}
              </span>
            </ng-template>
          }

          <!-- Color column template - FIXED: Added dblclick support -->
          @else if (column.type === 'color') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="color-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <div class="d-flex align-items-center">
                  @if (column.colorConfig?.showBox !== false) {
                    <div
                      class="color-box"
                      [ngClass]="{
                        'color-box-circle': column.colorConfig?.boxShape === 'circle',
                        'color-box-bordered': column.colorConfig?.bordered,
                      }"
                      [style.background-color]="getColorValue(dataItem[column.field!])"
                      [style.width]="column.colorConfig?.boxSize || '30px'"
                      [style.height]="column.colorConfig?.boxSize || '30px'"
                      [title]="dataItem[column.field!] || 'No color'"
                    ></div>
                  }
                  @if (column.colorConfig?.showLabel !== false) {
                    <span class="color-label ms-2">
                      {{ dataItem[column.field!] || '-' }}
                    </span>
                  }
                </div>
              </div>
            </ng-template>
          }

          <!-- Number column template -->
          @else if (column.type === 'number') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span
                class="number-cell"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                {{ formatNumber(dataItem[column.field!], column.format) }}
              </span>
            </ng-template>
          }

          <!-- Link column template - FIXED: Added tooltip -->
          @else if (column.type === 'link') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="link-cell-wrapper"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <a
                  href="{{ getLinkUrl(dataItem, column) }}"
                  [target]="'_blank'"
                  [title]="dataItem[column.field!] || ''"
                  class="link-cell"
                >
                  {{ dataItem[column.field!] || '-' }}
                </a>
              </div>
            </ng-template>
          }

          <!-- Badge column template -->
          @else if (column.type === 'badge') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="badge-cell-wrapper"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <span class="badge" [ngClass]="getBadgeClass(dataItem[column.field!], column)">
                  {{ dataItem[column.field!] }}
                </span>
              </div>
            </ng-template>
          }

          <!-- Checkbox column template (no dblclick - has its own interaction) -->
          @else if (column.type === 'checkbox') {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div class="checkbox-cell text-center">
                <input
                  type="checkbox"
                  class="k-checkbox"
                  [checked]="dataItem[column.field!]"
                  [disabled]="column.config?.disabled"
                  (change)="onCheckboxChange($event, dataItem, column)"
                />
              </div>
            </ng-template>
          }

          <!-- Custom template - FIXED: Added dblclick support wrapper -->
          @else if (column.template) {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="custom-cell-wrapper"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                <ng-container
                  *ngTemplateOutlet="
                    column.template;
                    context: { $implicit: dataItem, rowIndex: rowIndex }
                  "
                >
                </ng-container>
              </div>
            </ng-template>
          }

          <!-- Default display -->
          @else {
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div
                class="cell-content"
                [ngClass]="getCellClasses(column, dataItem)"
                [title]="getCellDblClickTooltip(column, dataItem)"
                (dblclick)="onCellDblClick($event, dataItem, column, rowIndex, $index)"
              >
                {{ formatCellValue(dataItem[column.field!], column) }}
              </div>
            </ng-template>
          }
        </kendo-grid-column>
      }

      <!-- Actions column -->
      @else if (column.type === 'actions' || column.actions) {
        <kendo-grid-column
          [title]="column.title || 'Actions'"
          [width]="column.width != null ? +column.width : 150"
          [locked]="column.locked === true"
          [headerClass]="'text-center'"
          [class]="'text-center'"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <div class="action-buttons">
              @for (action of column.actions; track action.id) {
                @if (isActionVisible(action, dataItem)) {
                  <button
                    type="button"
                    class="btn btn-sm"
                    [ngClass]="getActionClass(action, dataItem)"
                    [disabled]="isActionDisabled(action, dataItem)"
                    [title]="getActionTooltip(action, dataItem)"
                    (click)="onActionClick(action, dataItem)"
                  >
                    @if (getActionIcon(action, dataItem)) {
                      <i [class]="getActionIcon(action, dataItem)"></i>
                    }
                    @if (getActionText(action, dataItem)) {
                      <span [ngClass]="{ 'ms-1': getActionIcon(action, dataItem) }">
                        {{ getActionText(action, dataItem) }}
                      </span>
                    }
                  </button>
                }
              }
            </div>
          </ng-template>
        </kendo-grid-column>
      }
    }

    <!-- Detail Grid Template -->
    @if (enableDetailGrid && detailConfig) {
      <ng-template kendoGridDetailTemplate let-dataItem>
        <!-- Custom Detail Template -->
        @if (detailConfig.detailTemplate) {
          <ng-container
            *ngTemplateOutlet="
              (detailTemplate || config.detailTemplate || detailConfig.detailTemplate)!;
              context: { $implicit: dataItem }
            "
          >
          </ng-container>
        }

        <!-- Default Detail Grid -->
        @else if (detailConfig.detailGridConfig) {
          <div class="detail-grid-container">
            <ng-container *ngIf="getDetailData(dataItem) | async as detailData">
              <!-- Show grid only if there are records -->
              @if (detailData.data && detailData.data.length > 0) {
                <kendo-grid
                  [data]="detailData"
                  [pageSize]="20"
                  [pageable]="detailConfig.detailGridConfig.pageable !== false"
                  [sortable]="detailConfig.detailGridConfig.sortable === true"
                  [filterable]="detailConfig.detailGridConfig.filterable === true ? 'menu' : false"
                  [scrollable]="'scrollable'"
                  class="inner-detail-grid"
                  (cellClick)="onDetailCellClickEvent($event)"
                  (selectionChange)="onDetailSelectionChangeEvent($event)"
                >
                  @for (
                    detailColumn of detailConfig.detailGridConfig.columns;
                    track detailColumn.field || detailColumn.title
                  ) {
                    <kendo-grid-column
                      [field]="detailColumn.field || ''"
                      [title]="detailColumn.title"
                      [width]="detailColumn.width || 120"
                      [sortable]="detailColumn.sortable === true"
                      [filterable]="detailColumn.filterable === true"
                      [headerClass]="detailColumn.headerClass || ''"
                      [class]="detailColumn.className || ''"
                    >
                      <ng-template kendoGridCellTemplate let-detailItem>
                        <!-- Custom Template -->
                        @if (detailColumn.template) {
                          <ng-container
                            *ngTemplateOutlet="
                              detailColumn.template;
                              context: { $implicit: detailItem }
                            "
                          ></ng-container>
                        }

                        <!-- Image Column -->
                        @else if (detailColumn.type === 'image') {
                          <div class="image-cell">
                            <img
                              [src]="getImageSrc(detailItem, detailColumn)"
                              [alt]="getImageAlt(detailItem, detailColumn)"
                              [title]="getImageTitle(detailItem, detailColumn)"
                              class="grid-image"
                              [ngClass]="getImageClass(detailColumn)"
                              [style.width]="detailColumn.imageConfig?.width || '40px'"
                              [style.height]="detailColumn.imageConfig?.height || '40px'"
                              (error)="onImageError($event, detailColumn)"
                            />
                          </div>
                        }

                        <!-- Checkbox Column -->
                        @else if (detailColumn.type === 'checkbox') {
                          <div class="checkbox-cell">
                            <input
                              type="checkbox"
                              class="k-checkbox"
                              [checked]="detailItem[detailColumn.field!]"
                              (change)="
                                onDetailActionClick(
                                  { id: 'toggle', field: detailColumn.field || '' },
                                  detailItem
                                )
                              "
                            />
                          </div>
                        }

                        <!-- Boolean Column -->
                        @else if (detailColumn.type === 'boolean') {
                          <span class="boolean-cell">
                            <i
                              [class]="
                                detailItem[detailColumn.field!]
                                  ? 'bi bi-check-circle text-success'
                                  : 'bi bi-x-circle text-danger'
                              "
                            ></i>
                          </span>
                        }

                        <!-- Date Column -->
                        @else if (detailColumn.type === 'date') {
                          <span class="date-cell">
                            {{ formatDate(detailItem[detailColumn.field!], detailColumn.format) }}
                          </span>
                        }

                        <!-- Color Column -->
                        @else if (detailColumn.type === 'color') {
                          <div class="color-cell">
                            <div class="d-flex align-items-center">
                              @if (detailColumn.colorConfig?.showBox !== false) {
                                <div
                                  class="color-box"
                                  [ngClass]="{
                                    'color-box-circle':
                                      detailColumn.colorConfig?.boxShape === 'circle',
                                    'color-box-bordered': detailColumn.colorConfig?.bordered,
                                  }"
                                  [style.background-color]="
                                    getColorValue(detailItem[detailColumn.field!])
                                  "
                                  [style.width]="detailColumn.colorConfig?.boxSize || '30px'"
                                  [style.height]="detailColumn.colorConfig?.boxSize || '30px'"
                                  [title]="detailItem[detailColumn.field!] || 'No color'"
                                ></div>
                              }
                              @if (detailColumn.colorConfig?.showLabel !== false) {
                                <span class="color-label ms-2">
                                  {{ detailItem[detailColumn.field!] || '-' }}
                                </span>
                              }
                            </div>
                          </div>
                        }

                        <!-- Badge column template -->
                        @else if (detailColumn.type === 'badge') {
                          <span
                            class="badge"
                            [ngClass]="getBadgeClass(detailItem[detailColumn.field!], detailColumn)"
                          >
                            {{ detailItem[detailColumn.field!] }}
                          </span>
                        }

                        <!-- Actions Column -->
                        @else if (detailColumn.type === 'actions' && detailColumn.actions) {
                          <div class="action-buttons">
                            @for (action of detailColumn.actions; track action.id) {
                              @if (isActionVisible(action, detailItem)) {
                                <button
                                  type="button"
                                  class="btn btn-sm"
                                  [ngClass]="action.className || 'btn-outline-secondary'"
                                  [disabled]="isActionDisabled(action, detailItem)"
                                  [title]="action.tooltip || ''"
                                  (click)="onDetailActionClick(action, detailItem)"
                                >
                                  @if (action.icon) {
                                    <i [class]="action.icon"></i>
                                  }
                                  @if (action.text) {
                                    <span [ngClass]="{ 'ms-1': action.icon }">{{
                                      action.text
                                    }}</span>
                                  }
                                </button>
                              }
                            }
                          </div>
                        } @else if (detailColumn.type === 'link') {
                          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
                            <a
                              href="{{ getLinkUrl(dataItem, detailColumn) }}"
                              [target]="'_blank'"
                              [title]="dataItem[detailColumn.field!] || ''"
                              class="link-cell"
                            >
                              {{ dataItem[detailColumn.field!] || '-' }}
                            </a>
                          </ng-template>
                        }

                        <!-- Default Display -->
                        @else {
                          {{ formatCellValue(detailItem[detailColumn.field!], detailColumn) }}
                        }
                      </ng-template>
                    </kendo-grid-column>
                  }
                </kendo-grid>
              }

              <!-- Show empty state if no records -->
              @if (!detailData.data || detailData.data.length === 0) {
                <div class="detail-no-records">
                  <i class="bi bi-inbox text-muted"></i>
                  <p class="text-muted">No detail records found</p>
                </div>
              }
            </ng-container>

            <!-- Loading state -->
            @if (!(getDetailData(dataItem) | async)) {
              <div class="detail-loading">
                <div class="spinner-border text-primary">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            }
          </div>
        }
      </ng-template>
    }

    <!-- Simple detail template (non-grid) -->
    @else if (detailTemplate || config.detailTemplate) {
      <ng-template kendoGridDetailTemplate let-dataItem>
        <ng-container
          *ngTemplateOutlet="
            (detailTemplate || config.detailTemplate)!;
            context: { $implicit: dataItem }
          "
        >
        </ng-container>
      </ng-template>
    }

    <!-- Loading template -->
    <ng-template kendoGridLoadingTemplate>
      @if (loadingTemplate || config.loadingTemplate) {
        <ng-container *ngTemplateOutlet="(loadingTemplate || config.loadingTemplate)!">
        </ng-container>
      } @else {
        <div class="k-loading-mask">
          <div class="k-loading-text">Loading...</div>
          <div class="k-loading-image"></div>
        </div>
      }
    </ng-template>

    <!-- No records template -->
    <ng-template kendoGridNoRecordsTemplate>
      @if (noRecordsTemplate || config.noRecordsTemplate) {
        <ng-container *ngTemplateOutlet="(noRecordsTemplate || config.noRecordsTemplate)!">
        </ng-container>
      } @else {
        <div class="no-records text-center py-4">
          <i class="bi bi-inbox text-muted" style="font-size: 3rem"></i>
          <p class="text-muted mt-2 mb-0">No records found</p>
        </div>
      }
    </ng-template>
  </kendo-grid>
</div>
